import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KEY_VERSION } from '@app/app-components';
import { convertTimeZone, convertTimeZoneMinus } from '@app/shared/utils';
import { ValidadorSettingsUser } from '@app/shared/utils/validar_settings_user';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  interval,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  take,
  tap,
  timeout,
  timer
} from 'rxjs';
import { IToken, TokenService, User } from 'viggo-sistemas-core-web';
import { BuildDetails } from './build-details';
import { BuildDetailsService } from './build-details.service';

@Injectable({ providedIn: 'root' })
export class UpToDateBuildService {
  // Variáveis para controlar o estado do arquivo e tentativas
  private fileAvailabilityStatus = new BehaviorSubject<string>('unknown');
  private retrySubscription: Subscription | null = null;
  private hasReloadedPage = false;
  private isRetrying = false;

  private buildIsUpToDateSubject = new Subject<boolean>();

  constructor(
    protected buildDetailsService: BuildDetailsService,
    protected tokenService: TokenService,
    private httpClient: HttpClient
  ) {
    this.pollForBuildNumber();
  }

  public get buildIsUpToDate(): Observable<boolean> {
    return this.buildIsUpToDateSubject;
  }

  private pollForBuildNumber() {
    const pollInterval = 3000;

    const httpOptions = {
      headers: new HttpHeaders({
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0'
      }),
      // Adiciona opções para obter mais detalhes sobre erros
      observe: 'response' as const
    };

    // Evita iniciar uma nova requisição se já estiver retrying
    if (this.isRetrying) {
      return;
    }

    // Cancelar qualquer tentativa anterior de retry
    if (this.retrySubscription) {
      this.retrySubscription.unsubscribe();
      this.retrySubscription = null;
    }

    timer(pollInterval, pollInterval).subscribe(() => {
      const domain = this.tokenService.getDomain();
      if (domain && domain.name.toUpperCase() !== 'DEFAULT') {
        this.httpClient
          .get<BuildDetails>('assets/build-details.json', httpOptions)
          .pipe(
            // Extrai apenas o body da resposta para processar
            map(response => response.body),
            // Captura os erros mas permite que o observable continue
            catchError((error: any) => {
              // Determina o status do erro, mesmo que algumas propriedades sejam undefined
              let errorStatus = 'error';
              // Tenta obter o status do erro, com fallback para diferentes formatos
              if (error instanceof HttpErrorResponse) {
                if (error.status) {
                  if (error.status === 404) {
                    errorStatus = 'not-found';
                  } else if (error.status === 502) {
                    errorStatus = 'bad-gateway';
                  } else {
                    errorStatus = `error-${error.status}`;
                  }
                }
              } else if (typeof error === 'object' && error !== null) {
                // Caso o erro não seja HttpErrorResponse, mas tenha alguma estrutura
                if (error.status === 404) {
                  errorStatus = 'not-found';
                } else if (error.status === 502) {
                  errorStatus = 'bad-gateway';
                }
              }

              console.error('Erro ao acessar build-details.json:', error);
              // Atualiza o status de disponibilidade
              this.fileAvailabilityStatus.next(errorStatus);

              // Inicia o mecanismo de retry
              this.startRetryMechanism();

              // Retorna um observable vazio para não quebrar a cadeia
              return EMPTY;
            })
          )
          .subscribe(response => {
            // Se chegou aqui, o arquivo está acessível
            const previousStatus = this.fileAvailabilityStatus.value;
            this.fileAvailabilityStatus.next('available');

            // Limpa qualquer retry em andamento
            this.isRetrying = false;
            if (this.retrySubscription) {
              this.retrySubscription.unsubscribe();
              this.retrySubscription = null;
            }

            // Verifica se o response é válido
            if (!response) {
              console.warn('Resposta recebida, mas o corpo está vazio');
              this.startRetryMechanism();
              return;
            }

            // Se o arquivo estava inacessível anteriormente e agora está disponível,
            // recarregue a página uma única vez
            if (
              previousStatus !== 'unknown' &&
              previousStatus !== 'available' &&
              !this.hasReloadedPage
            ) {
              this.hasReloadedPage = true;
              // console.log(
              //   'Arquivo disponível novamente. Recarregando a página...'
              // );
              setTimeout(() => this.cleanCacheAndReload(), 100);
              return; // Sai do método para evitar processar a resposta antes do reload
            }

            // Processamento normal da resposta
            try {
              const newBuildNumber = response.buildNumber;
              const newBuildDh = response.buildDh
                ? convertTimeZone(new Date(response.buildDh))
                : new Date();
              const user = this.tokenService.getUser() as User;
              const token = this.tokenService.getToken() as IToken;
              if (user && this.tokenService.isLoggedIn()) {
                user.settings = new ValidadorSettingsUser(
                  user.settings,
                  user.roles.find(
                    rl => rl.name.toUpperCase() === 'SYSADMIN'
                  ) !== undefined
                ).getUserSettingsBody(user.settings) as any;
                if (user.settings['primeiro_acesso']) {
                  const dhToken = convertTimeZoneMinus(
                    new Date(token.created_at)
                  );
                  if (newBuildDh.getTime() > dhToken.getTime()) {
                    if (
                      user.settings['version']['web_version'] === 'UNKNOWN' ||
                      user.settings['version']['web_version'] !==
                        newBuildNumber.toString()
                    ) {
                      localStorage.setItem(
                        KEY_VERSION,
                        newBuildNumber.toString()
                      );
                      this.buildIsUpToDateSubject.next(false);
                    } else {
                      this.buildIsUpToDateSubject.next(true);
                    }
                  } else {
                    this.buildIsUpToDateSubject.next(true);
                  }
                } else if (
                  user.settings['version']['web_version'] === 'UNKNOWN'
                ) {
                  localStorage.setItem(KEY_VERSION, newBuildNumber.toString());
                  this.buildIsUpToDateSubject.next(false);
                } else {
                  localStorage.setItem(KEY_VERSION, newBuildNumber.toString());
                  this.buildIsUpToDateSubject.next(
                    user.settings['version']['web_version'] === newBuildNumber
                  );
                }
              }
            } catch (error) {
              console.error('Erro ao processar o build-details.json:', error);
              this.startRetryMechanism();
            }
          });
      }
    });
  }
  /**
   * Realiza limpeza de cache e recarrega a página com parâmetro de cache busting
   * Solução combinada para garantir que não ocorram ChunkLoadErrors
   */
  private cleanCacheAndReload(): void {
    console.log('Limpando cache do navegador e recarregando aplicação...');

    // 1. Preservar dados críticos
    // const preservedData: Record<string, string> = {};
    // this.CRITICAL_KEYS.forEach(key => {
    //   const value = localStorage.getItem(key);
    //   if (value) {
    //     preservedData[key] = value;
    //   }
    // });

    // 2. Limpar cache da Cache API (usado por aplicações modernas)
    if ('caches' in window) {
      caches
        .keys()
        .then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName).then(deleted => {
              if (deleted) {
                console.log(`Cache '${cacheName}' removido com sucesso`);
              }
            });
          });
        })
        .catch(err => {
          console.warn('Erro ao limpar cache:', err);
        });
    }

    // // 3. Restaurar dados críticos
    // Object.entries(preservedData).forEach(([key, value]) => {
    //   localStorage.setItem(key, value);
    // });

    // 4. Recarregar com parâmetro de cache busting
    setTimeout(() => {
      const timestamp = new Date().getTime();
      const currentUrl = window.location.href;
      const baseUrl = currentUrl.split('?')[0]; // Remove parâmetros existentes
      window.location.href = `${baseUrl}?fresh=${timestamp}`;
    }, 200); // Pequeno delay para garantir que a limpeza de cache seja concluída
  }

  // Método para iniciar o mecanismo de retry
  private startRetryMechanism(): void {
    if (this.isRetrying) {
      return; // Já existe um mecanismo de retry em andamento
    }

    this.isRetrying = true;

    // Configura intervalo de retry crescente (exponential backoff)
    const initialRetryInterval = 5000; // 5 segundos iniciais
    const maxRetryInterval = 60000; // máximo de 1 minuto
    let currentRetryInterval = initialRetryInterval;
    let retryCount = 0;

    // console.log(
    //   `Iniciando monitoramento. Próxima verificação em ${currentRetryInterval /
    //     1000} segundos`
    // );

    // Cancelar qualquer tentativa anterior
    if (this.retrySubscription) {
      this.retrySubscription.unsubscribe();
    }

    // Criamos uma função recursiva para fazer retry com intervalo dinâmico
    const retryWithBackoff = () => {
      this.retrySubscription = timer(currentRetryInterval)
        .pipe(
          take(1),
          tap(() => {
            retryCount++;

            // Aumenta o intervalo a cada 3 tentativas (opcional)
            if (
              retryCount > 0 &&
              retryCount % 3 === 0 &&
              currentRetryInterval < maxRetryInterval
            ) {
              currentRetryInterval = Math.min(
                currentRetryInterval * 2,
                maxRetryInterval
              );
            }

            // console.log(
            //   `Tentativa ${retryCount}: Verificando disponibilidade do arquivo...`
            // );

            // Verificação especial sem afetar o estado
            this.checkFileAvailability().then(isAvailable => {
              if (isAvailable) {
                // Se o arquivo estiver disponível, faz uma verificação completa
                this.isRetrying = false;
                this.pollForBuildNumber();
              } else {
                // Continua o retry
                retryWithBackoff();
              }
            });
          })
        )
        .subscribe();
    };

    // Inicia o processo de retry
    retryWithBackoff();
  }

  // Método para verificar apenas a disponibilidade do arquivo sem processar
  private checkFileAvailability(): Promise<boolean> {
    return new Promise(resolve => {
      this.httpClient
        .head('assets/build-details.json', {
          observe: 'response'
        })
        .pipe(
          timeout(5000), // Timeout para evitar esperas infinitas
          catchError(() => of(false))
        )
        .subscribe({
          next: response => {
            resolve(true);
          },
          error: () => {
            resolve(false);
          }
        });
    });
  }

  // Método para expor o status de disponibilidade para outros componentes
  getFileAvailabilityStatus(): Observable<string> {
    return this.fileAvailabilityStatus.asObservable();
  }

  // Método para verificar periodicamente os detalhes da build (para ser chamado no ngOnInit)
  initBuildDetailsMonitoring(): void {
    // Verificação inicial
    this.pollForBuildNumber();

    // Opcional: verificações periódicas (a cada 2 minutos)
    interval(120000).subscribe(() => {
      // Não inicie verificações se já existe um mecanismo de retry em andamento
      if (!this.isRetrying) {
        this.pollForBuildNumber();
      }
    });
  }
}
